import { resolveEmbedApiBaseUrl } from './context-api';
import {
    embedConfigToInitOptions,
    fetchWidgetEmbedConfig,
} from './settings-api';
import { initWidget } from './widget';

const VOTIQ_SCRIPT_PATTERN = /votiq\.widget/i;

function isVotiqEmbedScript(script: HTMLScriptElement): boolean {
    const src = script.getAttribute('src') ?? '';

    return VOTIQ_SCRIPT_PATTERN.test(src) || script.dataset.votiqEmbed === 'true';
}

function findBootstrapScript(): HTMLScriptElement | null {
    const current = document.currentScript;

    if (current instanceof HTMLScriptElement && isVotiqEmbedScript(current)) {
        return current;
    }

    const scripts = document.querySelectorAll<HTMLScriptElement>(
        'script[src]',
    );

    for (let index = scripts.length - 1; index >= 0; index -= 1) {
        const script = scripts[index];

        if (isVotiqEmbedScript(script)) {
            return script;
        }
    }

    return null;
}

function readProjectId(script: HTMLScriptElement): string | null {
    const fromData = script.dataset.projectId?.trim();

    if (fromData) {
        return fromData;
    }

    try {
        const url = new URL(script.src, window.location.href);

        return (
            url.searchParams.get('id')?.trim() ||
            url.searchParams.get('projectId')?.trim() ||
            null
        );
    } catch {
        return null;
    }
}

export async function autoBootstrapFromScriptTag(): Promise<boolean> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return false;
    }

    if (window.__VOTIQ_SKIP_AUTO_INIT__) {
        return false;
    }

    const script = findBootstrapScript();

    if (!script) {
        return false;
    }

    const projectId = readProjectId(script);

    if (!projectId) {
        return false;
    }

    const apiBaseUrl = resolveEmbedApiBaseUrl(
        script.dataset.apiBase?.trim() || undefined,
    );

    try {
        const config = await fetchWidgetEmbedConfig(projectId, apiBaseUrl);

        initWidget(embedConfigToInitOptions(config, apiBaseUrl));

        return true;
    } catch (error) {
        console.error('[Votiq] Failed to auto-start widget:', error);

        return false;
    }
}

export function scheduleAutoBootstrap(): void {
    const run = () => {
        void autoBootstrapFromScriptTag();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
        run();
    }
}

declare global {
    interface Window {
        /** Set to true before the embed script loads to use manual `Votiq.init()` only. */
        __VOTIQ_SKIP_AUTO_INIT__?: boolean;
    }
}
